// Client-side encryption for sensitive estate planning data
// Uses Web Crypto API for secure browser-based encryption

const ALGORITHM = 'AES-GCM';
const IV_LENGTH = 12; // 96 bits for GCM

// Generate a stable session key for encryption/decryption
async function generateSessionKey(): Promise<CryptoKey> {
  // Create a stable fingerprint that doesn't change on page refresh
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    // Use a stable session ID instead of changing timestamp
    sessionStorage.getItem('estate-plan-session-id') || 
    (() => {
      const sessionId = crypto.randomUUID();
      sessionStorage.setItem('estate-plan-session-id', sessionId);
      return sessionId;
    })()
  ].join('|');

  // Hash the fingerprint to create key material
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.digest('SHA-256', encoder.encode(fingerprint));
  
  // Import the key material as a cryptographic key
  return await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: ALGORITHM },
    false, // not extractable
    ['encrypt', 'decrypt']
  );
}

// Encrypt data before storing
export async function encryptData(data: string): Promise<string> {
  try {
    console.log('[Encryption] Starting encryption of data...');
    const key = await generateSessionKey();
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    // Generate a random IV for each encryption
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      dataBuffer
    );
    
    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);
    
    // Convert to base64 for storage
    const result = btoa(String.fromCharCode(...combined));
    console.log('[Encryption] Data encrypted successfully');
    return result;
  } catch (error) {
    console.error('[Encryption] Encryption failed:', error);
    // Fallback to unencrypted if encryption fails
    console.warn('[Encryption] Falling back to unencrypted storage due to encryption error');
    return data;
  }
}

// Decrypt data after retrieving
export async function decryptData(encryptedData: string): Promise<string> {
  try {
    console.log('[Decryption] Starting decryption...');
    
    // If data doesn't look encrypted (no base64), return as-is (backward compatibility)
    if (!isBase64(encryptedData)) {
      console.log('[Decryption] Data is not base64, treating as unencrypted');
      return encryptedData;
    }

    const key = await generateSessionKey();
    
    // Convert from base64
    const combined = new Uint8Array(
      atob(encryptedData)
        .split('')
        .map(char => char.charCodeAt(0))
    );
    
    // Extract IV and encrypted data
    const iv = combined.slice(0, IV_LENGTH);
    const encryptedBuffer = combined.slice(IV_LENGTH);
    
    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      encryptedBuffer
    );
    
    // Convert back to string
    const decoder = new TextDecoder();
    const result = decoder.decode(decryptedBuffer);
    console.log('[Decryption] Data decrypted successfully');
    return result;
  } catch (error) {
    console.error('[Decryption] Decryption failed:', error);
    
    // Try to parse as JSON directly (might be unencrypted data)
    try {
      JSON.parse(encryptedData);
      console.warn('[Decryption] Data appears to be unencrypted JSON, using as-is');
      return encryptedData;
    } catch {
      // If it's not valid JSON either, it's corrupted data
      console.warn('[Decryption] Data appears to be corrupted or encrypted with different key, clearing');
      return null;
    }
  }
}

// Check if a string is base64 encoded
function isBase64(str: string): boolean {
  try {
    return btoa(atob(str)) === str;
  } catch {
    return false;
  }
}

// Secure data erasure (overwrite memory)
export function secureErase(sensitiveString: string): void {
  if (typeof sensitiveString === 'string') {
    // In JavaScript, we can't truly overwrite memory, but we can at least 
    // clear references and encourage garbage collection
    sensitiveString = '';
    
    // Force garbage collection hint (not guaranteed)
    if (window.gc && typeof window.gc === 'function') {
      window.gc();
    }
  }
}

// Enhanced storage interface with encryption
export const secureStorage = {
  async setItem(key: string, value: string): Promise<void> {
    const encryptedValue = await encryptData(value);
    localStorage.setItem(`estate-plan-${key}`, encryptedValue);
  },
  
  async getItem(key: string): Promise<string | null> {
    const encryptedValue = localStorage.getItem(`estate-plan-${key}`);
    if (encryptedValue === null) {
      return null;
    }
    return await decryptData(encryptedValue);
  },
  
  removeItem(key: string): void {
    localStorage.removeItem(`estate-plan-${key}`);
  },
  
  // Secure clear - overwrite all estate planning data
  async clear(): Promise<void> {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('estate-plan-'));
    
    for (const key of keys) {
      // Overwrite with random data before deletion
      const randomData = crypto.getRandomValues(new Uint8Array(1024));
      const randomString = btoa(String.fromCharCode(...randomData));
      localStorage.setItem(key, randomString);
      
      // Then remove
      localStorage.removeItem(key);
    }
  }
};

// Data privacy utilities
export const privacy = {
  // Check if private browsing mode is detected
  isPrivateBrowsing(): boolean {
    try {
      const test = 'private-test';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return false;
    } catch {
      return true;
    }
  },
  
  // Get privacy-conscious storage info
  getStorageInfo(): { encrypted: boolean; private: boolean; secure: boolean } {
    return {
      encrypted: crypto && crypto.subtle ? true : false,
      private: this.isPrivateBrowsing(),
      secure: location.protocol === 'https:' || location.hostname === 'localhost'
    };
  },
  
  // Security warning for users
  getSecurityRecommendations(): string[] {
    const recommendations: string[] = [];
    const info = this.getStorageInfo();
    
    if (!info.encrypted) {
      recommendations.push('Your browser does not support encryption. Consider using a modern browser.');
    }
    
    if (!info.secure) {
      recommendations.push('For maximum security, use HTTPS when possible.');
    }
    
    if (!info.private) {
      recommendations.push('Consider using private/incognito browsing for maximum privacy.');
    }
    
    return recommendations;
  }
};